import { useEffect, useState, useRef } from 'react';
import { configService } from '@/services/configService';
import { deviceLockService } from '@/services/deviceLockService';
import { chimeService } from '@/services/chimeService';
import { metricsService } from '@/services/metricsService';
import { debug } from '@/config/constants';
import { configureAmplify } from '@/config/amplify';
import { AppConfig } from '@/types/config.types';
import { Layout } from '@/components/layout/Layout';
import { FeatureCarousel } from '@/components/features/FeatureCarousel';
import { GuidancePanel } from '@/components/features/GuidancePanel';
import { VoiceInterface } from '@/components/voice/VoiceInterface';
import { FEATURES } from '@/types/feature.types';
import { Feature } from '@/types/feature.types';
import { useConnect } from '@/hooks/useConnect';
import { useAuth } from '@/hooks/useAuth';
import { useQueue } from '@/hooks/useQueue';
import { ConnectionData } from '@/types/connect.types';
import { LoadingOverlay, LoadingButton } from '@/components/common';

// Inner component that uses auth-dependent hooks
function AuthenticatedApp({ config }: { config: AppConfig }) {
  const [error, setError] = useState<string | null>(null);
  const [isVoiceInterfaceOpen, setIsVoiceInterfaceOpen] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState<Feature | null>(null);
  const [connectionData, setConnectionData] = useState<ConnectionData | null>(null);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const currentSessionIdRef = useRef<string | null>(null);
  const [loadingFeatureIndex, setLoadingFeatureIndex] = useState<number | null>(null);
  
  const { startCall, endCall, isLoading: isStartingCall } = useConnect();
  const { getCredentials } = useAuth();
  const { isInQueue, queueId, queueJoinTime, queuePosition, queueLength, estimatedWaitTime, isConnected: isQueueConnected, joinQueue, leaveQueue, cleanupQueue, onCallReady } = useQueue();

  // Restore session ID from device lock on mount (handles page refresh during active call)
  useEffect(() => {
    const existingSessionId = deviceLockService.getActiveSessionId();
    if (existingSessionId) {
      debug.log(`Restored session ID from device lock: ${existingSessionId.substring(0, 30)}`);
      currentSessionIdRef.current = existingSessionId;
      setCurrentSessionId(existingSessionId);
    }
  }, []);

  const handleStartCall = async (feature: Feature | null, fromQueueId?: string) => {
    try {
      setSelectedFeature(feature);
      setIsVoiceInterfaceOpen(true);
      
      // Set loading state for the specific feature
      if (feature) {
        const featureIndex = FEATURES.findIndex(f => f.name === feature.name);
        setLoadingFeatureIndex(featureIndex);
      }
      
      // Reuse existing session ID if we have one (e.g. from queue), otherwise generate new
      const sessionId = currentSessionIdRef.current || `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      
      // Acquire device lock (same sessionId will succeed if we already hold it)
      if (!deviceLockService.acquireLock(sessionId)) {
        setError('You already have an active call in another tab. Please close other tabs or end that call first.');
        setIsVoiceInterfaceOpen(false);
        setLoadingFeatureIndex(null);
        return;
      }
      
      setCurrentSessionId(sessionId);
      currentSessionIdRef.current = sessionId;
      
      // Try to start call — pass queueId if coming from queue so backend can re-queue on race condition
      const connection = await startCall(sessionId, feature?.name, fromQueueId);
      setConnectionData(connection);
      setLoadingFeatureIndex(null);
    } catch (err) {
      console.error('Failed to start call:', err);
      setLoadingFeatureIndex(null);
      
      // Check if we should join queue
      if (err instanceof Error && (err.message.toLowerCase().includes('queue') || err.message.toLowerCase().includes('capacity'))) {
        try {
          // Keep the lock - user is still trying to connect (via queue)
          await joinQueue(feature?.name);
          // Queue display will show automatically via isInQueue state
        } catch (queueErr) {
          console.error('Failed to join queue:', queueErr);
          // Release lock if queue join failed
          if (currentSessionId) {
            deviceLockService.releaseLock(currentSessionId);
          }
          setIsVoiceInterfaceOpen(false);
          setError('Unable to start call or join queue. Please try again later.');
        }
      } else {
        // Release lock for non-queue errors
        if (currentSessionId) {
          deviceLockService.releaseLock(currentSessionId);
        }
        setIsVoiceInterfaceOpen(false);
        const errorMessage = err instanceof Error ? err.message : 'Failed to start call';
        setError(errorMessage);
      }
    }
  };

  const handleFeatureSelect = (feature: Feature) => {
    handleStartCall(feature);
  };

  // Handle call ready from queue
  useEffect(() => {
    onCallReady(() => {
      handleStartCall(selectedFeature, queueId || undefined);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFeature]);

  const handleGeneralCall = () => {
    handleStartCall(null);
  };

  const handleCloseVoiceInterface = async () => {
    // Release device lock
    if (currentSessionIdRef.current) {
      deviceLockService.releaseLock(currentSessionIdRef.current);
      await endCall(currentSessionIdRef.current);
    }
    
    // Clean up queue state if it wasn't already cleaned
    if (queueId) {
      cleanupQueue();
    }
    
    setIsVoiceInterfaceOpen(false);
    setSelectedFeature(null);
    setConnectionData(null);
    setCurrentSessionId(null);
    currentSessionIdRef.current = null;
    setLoadingFeatureIndex(null);
  };

  const handleCancelQueue = async () => {
    // Record QUEUE_ABANDONED metric
    if (queueId && queueJoinTime) {
      try {
        const credentials = await getCredentials();
        const waitTime = Math.floor((Date.now() - queueJoinTime) / 1000);
        await metricsService.recordMetric(credentials, {
          metricType: 'QUEUE_ABANDONED',
          sessionId: currentSessionIdRef.current || '',
          queueId,
          waitTime,
          featureName: selectedFeature?.name,
        });
      } catch (err) {
        console.error('Failed to record QUEUE_ABANDONED metric:', err);
      }
    }
    // Release device lock when canceling queue
    if (currentSessionIdRef.current) {
      deviceLockService.releaseLock(currentSessionIdRef.current);
    }
    currentSessionIdRef.current = null;
    setCurrentSessionId(null);
    await leaveQueue();
  };

  return (
    <Layout>
          {error && (
            <div 
              style={{
                padding: '16px 20px',
                marginBottom: '24px',
                background: '#dc3545',
                border: '2px solid #b02a37',
                borderRadius: '8px',
                color: '#ffffff',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                boxShadow: '0 2px 8px rgba(220, 53, 69, 0.3)',
              }}
              role="alert"
              aria-live="assertive"
            >
              <span style={{ fontWeight: '500' }}>{error}</span>
              <button
                onClick={() => setError(null)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#ffffff',
                  cursor: 'pointer',
                  fontSize: '24px',
                  padding: '0 8px',
                  fontWeight: 'bold',
                }}
                aria-label="Dismiss error message"
              >
                ×
              </button>
            </div>
          )}

          {/* Hero section */}
          <div style={{
            textAlign: 'center',
            marginBottom: '32px',
            padding: '0 16px',
          }}>
            <h2 style={{
              fontSize: '28px',
              fontWeight: 'bold',
              marginBottom: '12px',
              color: '#ffffff',
            }}>
              Experience Amazon Connect AI in Action
            </h2>
            <p style={{
              fontSize: '16px',
              color: '#cccccc',
              maxWidth: '640px',
              margin: '0 auto',
              lineHeight: '1.6',
            }}>
              Try our interactive demo — talk to an AI-powered agent to explore agentic self-service, predictive insights, manager assistance, and more. All powered by Amazon Connect.
            </p>
          </div>

          <FeatureCarousel 
            features={FEATURES}
            onFeatureSelect={handleFeatureSelect}
            loadingFeatureIndex={loadingFeatureIndex}
          />
          
          <LoadingButton
            onClick={handleGeneralCall}
            disabled={isStartingCall || isInQueue}
            isLoading={isStartingCall}
            loadingText="Starting Call..."
            variant="primary"
            fullWidth
            style={{
              maxWidth: '400px',
              height: '56px',
              fontSize: '18px',
              margin: '24px auto 0',
              display: 'block',
            }}
            aria-label="Talk to AI Agent"
          >
            {isInQueue ? 'In Queue...' : 'Talk to AI Agent'}
          </LoadingButton>
          

          <VoiceInterface
            isOpen={isVoiceInterfaceOpen || isInQueue}
            onClose={handleCloseVoiceInterface}
            feature={selectedFeature}
            connectionData={connectionData}
            sessionId={currentSessionId}
            queueId={queueId}
            queueJoinTime={queueJoinTime}
            maxDuration={config?.MAX_DURATION ? config.MAX_DURATION * 60 : 600}
            isInQueue={isInQueue}
            queuePosition={queuePosition}
            queueLength={queueLength}
            estimatedWaitTime={estimatedWaitTime}
            isQueueConnected={isQueueConnected}
            onCancelQueue={handleCancelQueue}
          />
        </Layout>
  );
}

// Main App component that handles config loading
function App() {
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function initializeApp() {
      try {
        const loadedConfig = await configService.loadConfig();
        configureAmplify(loadedConfig);
        setConfig(loadedConfig);
        
        // Pre-warm Voice Focus in background (runs async, doesn't block)
        if (loadedConfig?.ENABLE_VOICE_FOCUS !== false) {
          chimeService.warmupVoiceFocus();
        }
      } catch (err) {
        console.error('Failed to initialize app:', err);
        setError(err instanceof Error ? err.message : 'Failed to load configuration');
      } finally {
        setLoading(false);
      }
    }

    initializeApp();
  }, []);

  if (loading) {
    return <LoadingOverlay message="Loading application..." isFullScreen />;
  }

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        flexDirection: 'column',
        gap: '16px',
        padding: '24px'
      }}>
        <h2 style={{ color: '#d13212' }}>Configuration Error</h2>
        <p>{error}</p>
        <button 
          onClick={() => window.location.reload()}
          style={{
            padding: '12px 24px',
            background: '#0073BB',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '16px'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return <AuthenticatedApp config={config!} />;
}

export default App;
